/**
 * Created by renlaydel on 04/12/2017.
 */
import without from 'lodash/without';
import uniq from 'lodash/uniq';
import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { InventoryReturnForm } from '../../../../constants/constant_form_names';
import { BATCH, SERIALIZED } from '../../../../constants/constant_item_types';
import {
  CLOSED,
  CLOSED_ABANDONED,
  ERROR,
  PROCESSING,
} from '../../../../constants/constant_transaction_status';

const selector = formValueSelector(InventoryReturnForm);

function getFormItemLines(state) {
  return selector(state, 'lines') || [];
}

function getFormLineByIndex(state, index) {
  return selector(state, `lines[${index}]`) || {};
}

function getFormStatus(state) {
  return selector(state, 'status') || {};
}

function getFormRunStatus(state) {
  return selector(state, 'run_status');
}

function getFormPurchaseReceiptId(state) {
  return selector(state, 'purchase_receipt_id');
}

function getFormReferenceId(state) {
  return selector(state, 'reference_id');
}

export const getSubsidiary = createSelector(
  [getFormSubsidiary],
  (subsidiary) => {
    return subsidiary;
  },
);

function getFormSubsidiary(state) {
  const subsidiary = selector(state, 'subsidiary') || {};
  return subsidiary;
}

function getFormId(state) {
  const { id, _id } = selector(state, 'id', '_id');
  return id || _id;
}

function getFormCurrency(state) {
  return selector(state, 'currency') || {};
}

function getFormLocationCurrency(state) {
  return selector(state, 'location_currency') || {};
}

function getFormSupplierCurrency(state) {
  return selector(state, 'supplier_currency') || {};
}

function getFormLocation(state) {
  return selector(state, 'location') || {};
}

function getPropViewMode(state, viewMode) {
  return viewMode;
}

function getEnableItemPartQuantityPreference(state) {
  return ((state.preference || {}).settings || {}).enable_item_part_quantity;
}

export const getLocationId = createSelector(
  [getFormLocation],
  (location) => location.id,
);

export const isExistingReturn = createSelector(
  [getFormId],
  (formId) => Boolean(formId),
);

export const hasRunStatus = createSelector(
  [getFormRunStatus],
  (runStatus) => {
    return Boolean(runStatus);
  },
);

export const viewModeForm = createSelector(
  [getFormStatus, hasRunStatus, getPropViewMode],
  (status, hasRunStatus, viewMode) => {
    return (
      [CLOSED_ABANDONED, CLOSED, ERROR, PROCESSING].indexOf(status.cid) > -1 ||
      hasRunStatus ||
      viewMode
    );
  },
);

export const hasOnEditModeLine = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return false;
    return itemLines.some((itemLine) => {
      return (
        itemLine &&
        !itemLine.quantity &&
        !itemLine.item_id &&
        itemLine.onEditMode
      );
    });
  },
);

export const getEditModeLineIndex = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return null;
    return itemLines.findIndex((itemLine) => {
      return itemLine && itemLine.onEditMode;
    });
  },
);

export const getValidCurrency = createSelector(
  [getFormLocationCurrency, getFormSupplierCurrency],
  (locationCurrency, supplierCurrency) => {
    const locationCurrencyId = (locationCurrency || {}).id;
    const supplierCurrencyId = (supplierCurrency || {}).id;
    const includedCurrencies = [];

    if (locationCurrencyId) includedCurrencies.push(locationCurrencyId);
    if (supplierCurrencyId) includedCurrencies.push(supplierCurrencyId);

    return uniq(includedCurrencies);
  },
);

export const hasBatchSerializedItems = createSelector(
  [getFormItemLines],
  (itemLines) => {
    if (!itemLines.length) return false;
    return itemLines.some((itemLine) => {
      if (!itemLine) return false;
      return [BATCH, SERIALIZED].includes(itemLine.item_type);
    });
  },
);

export const getLocationCurrencyCode = createSelector(
  [getFormLocationCurrency],
  (locationCurrency) => {
    return (locationCurrency || {}).code;
  },
);

export const getLocationCurrencyId = createSelector(
  [getFormLocationCurrency],
  (locationCurrency) => {
    return (locationCurrency || {}).id;
  },
);

export const getUnfilledLineItemIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.findIndex((line) => {
      return line && !line.quantity && !line.item_id;
    });
  },
);

export const getSubTotal = createSelector(
  [getFormItemLines],
  (itemLines) => {
    return itemLines.reduce((subTotal, itemLine) => {
      const { total_ex_tax } = itemLine;
      return subTotal + Number(total_ex_tax);
    }, 0);
  },
);

export const getTotalTax = createSelector(
  [getFormItemLines],
  (itemLines) => {
    return itemLines.reduce((totalTax, itemLine) => {
      const { total_ex_tax, total } = itemLine;
      const tax = Number(total) - Number(total_ex_tax);
      return totalTax + tax;
    }, 0);
  },
);

export const getTotal = createSelector(
  [getFormItemLines],
  (itemLines) => {
    return itemLines.reduce((totalAmount, itemLine) => {
      const { total } = itemLine;
      return Number(total) + totalAmount;
    }, 0);
  },
);

export const getCurrencyCode = createSelector(
  [getFormCurrency],
  (currency) => (currency || {}).code,
);

export const getCurrencySymbol = createSelector(
  [getFormCurrency],
  (currency) => (currency || {}).symbol,
);

export const getReferenceId = createSelector(
  [getFormReferenceId],
  (referenceId) => referenceId,
);

export const hasPurchaseReceipt = createSelector(
  [getFormPurchaseReceiptId],
  (purchaseReceiptId) => {
    return Boolean(purchaseReceiptId);
  },
);

export const hasPurchaseReceiptLine = createSelector(
  [getFormLineByIndex],
  (line) => {
    return Boolean(line.pr_line_id);
  },
);

export const getLineItemName = createSelector(
  [getFormLineByIndex],
  (line) => (line.item || {}).name,
);

export const getLineItemUom = createSelector(
  [getFormLineByIndex],
  (line) => line.item_uom,
);

export const getUnfilledLineReferenceIdIndex = createSelector(
  [getFormLineByIndex],
  (itemLine) => {
    return (itemLine.reference_ids || []).findIndex((referenceId) => {
      return referenceId && !referenceId.reference;
    });
  },
);

export const getEditModeLineReferenceIdIndex = createSelector(
  [getFormLineByIndex],
  (itemLine) => {
    if (!(itemLine.reference_ids || []).length) return null;
    return itemLine.reference_ids.findIndex((itemLineRefId) => {
      return itemLineRefId && itemLineRefId.onRefLineEditMode;
    });
  },
);

export const getExcludedItems = createSelector(
  [getFormItemLines, getFormLineByIndex],
  (itemLines, line) => {
    const selectedItemIds = itemLines.map((itemLine) => itemLine.item_id);
    const { item_id } = line;
    const excludedItemIds = without(
      selectedItemIds,
      undefined,
      null,
      '',
      item_id,
    );
    return excludedItemIds;
  },
);

export const isLineBatchSerializedItem = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { item_type } = line;
    return [BATCH, SERIALIZED].indexOf(item_type) > -1;
  },
);

export const getLineTax = createSelector(
  [getFormLineByIndex],
  (line) => {
    const { total, total_ex_tax } = line;
    return (total || 0) - (total_ex_tax || 0);
  },
);

export const getFormLines = createSelector(
  [getFormItemLines],
  (lines) => lines,
);

export const hasLines = createSelector(
  [getFormItemLines],
  (lines) => Boolean(lines.length),
);

export const getLineId = createSelector(
  [getFormLineByIndex],
  (line) => line.line_id,
);

export const isLineFetchingItemLocation = createSelector(
  [getFormLineByIndex],
  (line) => line.isFetchingItemLocation,
);

export const isLineFetchingTaxGroup = createSelector(
  [getFormLineByIndex],
  (line) => line.isFetchingTaxGroup,
);

export const hasLineNextStatus = createSelector(
  [getFormLineByIndex],
  (line) => Boolean(line.next_status_id),
);

export const getLineOnEditMode = createSelector(
  [getFormLineByIndex],
  (line) => line.onEditMode,
);

export const isPartQuantity = createSelector(
  [getFormLineByIndex, getEnableItemPartQuantityPreference],
  (line, enableItemPartQuantity) => {
    const { item } = line;
    return enableItemPartQuantity && (item || {}).enable_part_quantity;
  },
);
